/**
 * Die Welt außerhalb des Unternehmens.
 *
 * Drei Ansichten auf dieselbe Frage: *Was passiert da draußen, und was heißt
 * das für mich?* Die Weltlage zeigt Konjunktur und Preise, die Fachpresse
 * erklärt sie, der Markt zeigt, mit wem man es zu tun hat.
 *
 * Wichtig für den Ton: Die Seite wertet nicht. Sie sagt nicht „Achtung, deine
 * Kosten steigen", sondern zeigt einen Index und lässt den Spieler den Schluss
 * ziehen. Wer bevormundet wird, hört auf mitzudenken.
 */
import { useEffect, useState } from 'react';
import type { Competitor, NewsItem, Regime, RegionFactors, RegionTrend, TechWave, TrendState } from '@hosting-sim/core';
import { INDEX_LABEL, IndexKeys, NEWS_CATEGORY_LABEL, NewsCategories, ARCHETYPE_LABEL, STRATEGY_LABEL, trendArrow, tickToDate } from '@hosting-sim/core';
import { Badge, Card, EmptyState, PageHeader, Spinner, Stat, StatGrid, Tabs, type Tone } from '../components/ui';
import { LineChart } from '../components/charts/Charts';
import { gameDay, num, pct } from '../lib/format';
import { api } from '../lib/api';
import { useGame, useMetrics } from '../store/game';

const PRODUCT_LABEL: Record<string, string> = {
  'shared-hosting': 'Shared Hosting',
  vps: 'VPS',
  dedicated: 'Dedicated',
  colocation: 'Colocation',
  storage: 'Storage',
  domain: 'Domains',
  ssl: 'SSL',
  backup: 'Backup',
  mail: 'Mail',
  'transit-sale': 'Transitverkauf',
};

/** Ein Index über 1,0 ist teuer und damit schlecht – außer bei der Konjunktur. */
function indexTone(key: string, value: number): Tone {
  const bad = value > 1.15;
  const good = value < 0.9;
  if (key === 'konjunktur') return value > 1.1 ? 'success' : value < 0.9 ? 'danger' : 'neutral';
  return bad ? 'danger' : good ? 'success' : 'neutral';
}

export function WorldPage() {
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  const day = Math.floor((metrics?.meta.tick ?? 0) / 24);

  const [data, setData] = useState<Awaited<ReturnType<typeof api.world>> | null>(null);
  const [tab, setTab] = useState<'lage' | 'markt' | 'regionen'>('lage');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    api
      .world(gameId)
      .then((result) => !cancelled && setData(result))
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [gameId, day]);

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="Weltlage" />
        <EmptyState title="Die Weltlage konnte nicht geladen werden." description={error} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-4">
        <PageHeader title="Weltlage" />
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  const monthsLeft = Math.max(
    0,
    Math.round((data.economy.regime.endsAtTick - (metrics?.meta.tick ?? 0)) / (24 * 30)),
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Weltlage"
        description="Was sich außerhalb des eigenen Unternehmens bewegt – und was es kostet."
      />

      <RegimeCard regime={data.regime} monthsLeft={monthsLeft} />

      <Tabs
        active={tab}
        onChange={(id) => setTab(id as typeof tab)}
        tabs={[
          { id: 'lage', label: 'Preise' },
          { id: 'markt', label: 'Nachfrage', badge: data.waves.length || undefined },
          { id: 'regionen', label: 'Regionen', badge: data.regionTrends.length || undefined },
        ]}
      />

      {tab === 'lage' ? (
        <Prices economy={data.economy} />
      ) : tab === 'markt' ? (
        <Demand trends={data.trends} waves={data.waves} />
      ) : (
        <Regions regions={data.regions} trends={data.regionTrends} />
      )}
    </div>
  );
}

function RegimeCard({ regime, monthsLeft }: { regime: Regime; monthsLeft: number }) {
  const tone: Tone =
    regime.key === 'boom' ? 'success' : regime.key === 'normal' ? 'neutral' : regime.key === 'rezession' ? 'danger' : 'warning';

  return (
    <Card>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="label">Konjunkturlage</span>
            <Badge tone={tone}>{regime.label}</Badge>
          </div>
          <p className="mt-1 text-sm text-panel-700 dark:text-panel-200">{regime.description}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="label">Voraussichtlich noch</p>
          <p className="tabular text-lg font-semibold">{monthsLeft} Monate</p>
        </div>
      </div>
    </Card>
  );
}

function Prices({ economy }: { economy: Awaited<ReturnType<typeof api.world>>['economy'] }) {
  return (
    <>
      <StatGrid cols={4}>
        {IndexKeys.map((key) => {
          const value = economy.indices[key].value;
          const delta = Math.round((value - 1) * 100);
          return (
            <Stat
              key={key}
              label={INDEX_LABEL[key]}
              value={`${delta > 0 ? '+' : ''}${delta} %`}
              tone={
                indexTone(key, value) === 'danger' ? 'negative' : indexTone(key, value) === 'success' ? 'positive' : 'neutral'
              }
              hint={
                Math.abs(economy.indices[key].target - value) > 0.03
                  ? `Ziel ${Math.round((economy.indices[key].target - 1) * 100)} % – die Anpassung läuft noch`
                  : 'am Zielwert'
              }
            />
          );
        })}
      </StatGrid>

      <Card title="Preisentwicklung" subtitle="Monatswerte seit Beginn der Partie" className="mt-4">
        <LineChart
          series={[
            { label: 'Konjunktur', points: economy.history.konjunktur, color: '#21a471' },
            { label: 'Strom', points: economy.history.strom, color: '#f59e0b' },
            { label: 'Hardware', points: economy.history.hardware, color: '#3b82f6' },
            { label: 'IPv4', points: economy.history.ipv4, color: '#a855f7' },
          ]}
          height={220}
        />
        <p className="mt-3 border-t divider pt-3 text-xs text-panel-500 dark:text-panel-400">
          1,00 entspricht dem Normalwert. Preise bewegen sich träge – ein Regimewechsel braucht Monate, bis er
          vollständig in der Rechnung ankommt.
        </p>
      </Card>
    </>
  );
}

function Demand({
  trends,
  waves,
}: {
  trends: TrendState;
  waves: { key: string; endsAtTick: number; wave?: TechWave }[];
}) {
  const entries = Object.entries(trends.demand).sort((a, b) => b[1] - a[1]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Nachfragemix" subtitle="Wohin sich der Markt bewegt">
        <ul className="space-y-1.5">
          {entries.map(([kind, value]) => (
            <li key={kind} className="flex items-baseline justify-between gap-3 text-sm">
              <span>{PRODUCT_LABEL[kind] ?? kind}</span>
              <span className="flex items-baseline gap-2">
                <span
                  className={
                    'tabular text-xs ' +
                    (value >= 1.07
                      ? 'text-accent-600 dark:text-accent-400'
                      : value <= 0.93
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-panel-500')
                  }
                >
                  {Math.round((value - 1) * 100) > 0 ? '+' : ''}
                  {Math.round((value - 1) * 100)} %
                </span>
                <span className="w-4 text-center text-base">{trendArrow(value)}</span>
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Laufende Entwicklungen">
        {waves.length === 0 ? (
          <p className="text-xs text-panel-500 dark:text-panel-400">
            Zurzeit bewegt sich der Markt nur entlang seiner langfristigen Grundrichtung.
          </p>
        ) : (
          <ul className="space-y-3">
            {waves.map((entry) => (
              <li key={entry.key}>
                <p className="text-sm font-medium">{entry.wave?.headline ?? entry.key}</p>
                <p className="mt-0.5 text-xs text-panel-500 dark:text-panel-400">{entry.wave?.body}</p>
                <p className="mt-1 text-2xs text-panel-400 dark:text-panel-500">
                  bis {gameDay(tickToDate(entry.endsAtTick))}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Regions({
  regions,
  trends,
}: {
  regions: Record<string, RegionFactors>;
  trends: { key: string; endsAtTick: number; trend?: RegionTrend }[];
}) {
  // Nur was sich bewegt hat – 42 Zeilen mit lauter 1,00 sind keine Information.
  const moved = Object.entries(regions)
    .filter(([, f]) => Math.abs(f.demand - 1) > 0.04 || Math.abs(f.power - 1) > 0.04 || Math.abs(f.rent - 1) > 0.04)
    .sort((a, b) => b[1].demand - a[1].demand);

  return (
    <div className="space-y-4">
      <Card title="Laufende Regionalentwicklungen">
        {trends.length === 0 ? (
          <p className="text-xs text-panel-500 dark:text-panel-400">Zurzeit entwickeln sich alle Regionen gleichmäßig.</p>
        ) : (
          <ul className="space-y-3">
            {trends.map((entry) => (
              <li key={entry.key}>
                <p className="text-sm font-medium">{entry.trend?.headline ?? entry.key}</p>
                <p className="mt-0.5 text-xs text-panel-500 dark:text-panel-400">{entry.trend?.body}</p>
                <p className="mt-1 text-2xs text-panel-400 dark:text-panel-500">
                  bis {gameDay(tickToDate(entry.endsAtTick))}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Veränderte Standorte" subtitle="Abweichung vom Katalogwert">
        {moved.length === 0 ? (
          <p className="text-xs text-panel-500 dark:text-panel-400">Noch hat sich kein Standort spürbar verändert.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b divider text-left">
                  <th className="py-1.5 pr-3 font-medium">Standort</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Nachfrage</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Strom</th>
                  <th className="py-1.5 text-right font-medium">Miete</th>
                </tr>
              </thead>
              <tbody>
                {moved.map(([key, factors]) => (
                  <tr key={key} className="border-b divider last:border-0">
                    <td className="py-1.5 pr-3">{key.replace('loc-', '').toUpperCase()}</td>
                    <Factor value={factors.demand} higherIsBetter />
                    <Factor value={factors.power} />
                    <Factor value={factors.rent} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Factor({ value, higherIsBetter = false }: { value: number; higherIsBetter?: boolean }) {
  const delta = Math.round((value - 1) * 100);
  const good = higherIsBetter ? delta > 0 : delta < 0;
  return (
    <td
      className={
        'tabular py-1.5 pr-3 text-right ' +
        (Math.abs(delta) < 4
          ? 'text-panel-400'
          : good
            ? 'text-accent-600 dark:text-accent-400'
            : 'text-red-600 dark:text-red-400')
      }
    >
      {delta > 0 ? '+' : ''}
      {delta} %
    </td>
  );
}

// ---------------------------------------------------------------------------
// Fachpresse
// ---------------------------------------------------------------------------

export function News() {
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  const day = Math.floor((metrics?.meta.tick ?? 0) / 24);

  const [items, setItems] = useState<NewsItem[] | null>(null);
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    api
      .news(gameId, category ?? undefined)
      .then((result) => !cancelled && setItems(result.items))
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, [gameId, category, day]);

  return (
    <div className="space-y-4">
      <PageHeader title="Fachpresse" description="Was in der Branche passiert – und warum sich Preise bewegen." />

      <Tabs
        active={category ?? 'alle'}
        onChange={(id) => setCategory(id === 'alle' ? null : id)}
        tabs={[
          { id: 'alle', label: 'Alle' },
          ...NewsCategories.map((key) => ({ id: key, label: NEWS_CATEGORY_LABEL[key] })),
        ]}
      />

      {!items ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Noch keine Meldungen"
          description="Die Fachpresse berichtet, sobald sich in der Welt etwas Nennenswertes bewegt."
        />
      ) : (
        <div className="space-y-2">
          {items.map((entry, index) => (
            <Card key={`${entry.tick}-${index}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <Badge tone={entry.relevance === 'wichtig' ? 'warning' : 'neutral'}>
                  {NEWS_CATEGORY_LABEL[entry.category]}
                </Badge>
                <h3 className="min-w-0 flex-1 text-sm font-semibold">{entry.headline}</h3>
                <span className="tabular shrink-0 text-2xs text-panel-400 dark:text-panel-500">
                  {gameDay(tickToDate(entry.tick))}
                </span>
              </div>
              <p className="mt-1.5 text-sm text-panel-600 dark:text-panel-300">{entry.body}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Markt
// ---------------------------------------------------------------------------

export function Market() {
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  const month = Math.floor((metrics?.meta.tick ?? 0) / (24 * 30));

  const [data, setData] = useState<Awaited<ReturnType<typeof api.competitors>> | null>(null);
  const [segment, setSegment] = useState<string>('shared-hosting');

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    api
      .competitors(gameId)
      .then((result) => !cancelled && setData(result))
      .catch(() => !cancelled && setData(null));
    return () => {
      cancelled = true;
    };
  }, [gameId, month]);

  if (!data) {
    return (
      <div className="space-y-4">
        <PageHeader title="Markt" />
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  const active = data.firms.filter((f) => f.state === 'aktiv');
  const gone = data.firms.filter((f) => f.state !== 'aktiv');

  /** Rangliste im gewählten Segment, mit dem eigenen Unternehmen darin. */
  const ranking: { name: string; share: number; own: boolean; firm?: Competitor }[] = [
    ...active
      .filter((f) => (f.share[segment as keyof typeof f.share] ?? 0) > 0)
      .map((f) => ({ name: f.name, share: f.share[segment as keyof typeof f.share] ?? 0, own: false, firm: f })),
    { name: metrics?.company.name ?? 'Eigenes Unternehmen', share: data.own[segment] ?? 0, own: true },
  ].sort((a, b) => b.share - a.share);

  return (
    <div className="space-y-4">
      <PageHeader title="Markt" description="Mit wem man es zu tun hat – und wie viel Platz noch frei ist." />

      <StatGrid cols={4}>
        <Stat label="Aktive Wettbewerber" value={num(active.length)} hint={`${gone.length} ausgeschieden`} />
        <Stat
          label="Belegt im Segment"
          value={pct(data.occupied[segment] ?? 0, 0)}
          hint={PRODUCT_LABEL[segment] ?? segment}
        />
        <Stat
          label="Preisniveau der Konkurrenz"
          value={`${Math.round(((data.priceLevel[segment] ?? 1) - 1) * 100)} %`}
          tone={(data.priceLevel[segment] ?? 1) < 0.95 ? 'negative' : 'neutral'}
          hint="gegenüber dem Referenzpreis"
        />
        <Stat label="Eigener Anteil" value={pct(data.own[segment] ?? 0, 2)} tone="positive" hint="im gewählten Segment" />
      </StatGrid>

      <Tabs
        active={segment}
        onChange={setSegment}
        tabs={['shared-hosting', 'vps', 'dedicated', 'colocation', 'storage'].map((key) => ({
          id: key,
          label: PRODUCT_LABEL[key] ?? key,
        }))}
      />

      <Card title="Rangliste" subtitle={PRODUCT_LABEL[segment] ?? segment}>
        <ul className="space-y-1.5">
          {ranking.map((entry, index) => (
            <li
              key={`${entry.name}-${index}`}
              className={
                'flex flex-wrap items-baseline gap-2 rounded px-2 py-1.5 text-sm ' +
                (entry.own ? 'bg-accent-50 font-medium dark:bg-accent-950/40' : '')
              }
            >
              <span className="tabular w-6 shrink-0 text-panel-400">{index + 1}.</span>
              <span className="min-w-0 flex-1 truncate">{entry.name}</span>
              {entry.firm && (
                <>
                  <Badge tone="muted">{ARCHETYPE_LABEL[entry.firm.archetype]}</Badge>
                  <Badge tone={entry.firm.strategy === 'preiskampf' ? 'warning' : 'neutral'}>
                    {STRATEGY_LABEL[entry.firm.strategy]}
                  </Badge>
                </>
              )}
              <span className="tabular w-16 shrink-0 text-right">{pct(entry.share, 2)}</span>
            </li>
          ))}
        </ul>
      </Card>

      {gone.length > 0 && (
        <Card title="Ausgeschieden">
          <ul className="space-y-1">
            {gone.map((firm) => (
              <li key={firm.id} className="flex items-baseline justify-between gap-3 text-sm">
                <span>{firm.name}</span>
                <span className="flex items-baseline gap-2">
                  <Badge tone={firm.state === 'insolvent' ? 'danger' : 'muted'}>
                    {firm.state === 'insolvent' ? 'Insolvenz' : 'übernommen'}
                  </Badge>
                  {firm.leftAtTick !== undefined && (
                    <span className="tabular text-2xs text-panel-400">{gameDay(tickToDate(firm.leftAtTick))}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
